import React from 'react';
import { View, Animated, TouchableWithoutFeedback, StyleSheet, Text, TouchableOpacity } from 'react-native';
import TransactionScreen from '../screens/wallet/childScreens/TransactionScreen';
import { globalFontStyle, globalStyle } from '../assets';
import RupeeVal from './RupeeVal';
import { globalConfigurableData, GlobalScreens } from '../utils/globalConstant';
import ReactDOM from 'react-dom';
import FastImage from '../common/FastImage';
import { Icon } from '@iconify/react';

const TransactionDrawer = ({ open, onClose, transaction, typeTxn, breakupData, showBreakup }) => {
  const [slideAnim] = React.useState(new Animated.Value(100));
  const [overlayAnim] = React.useState(new Animated.Value(0));
  const [portalContainer] = React.useState(() => {
    if (typeof document !== 'undefined') {
      const div = document.createElement('div');
      div.style.position = 'relative';
      div.style.zIndex = '9999';
      return div;
    }
    return null;
  });

  React.useEffect(() => {
    if (typeof document !== 'undefined' && portalContainer) {
      document.body.appendChild(portalContainer);
      return () => {
        document.body.removeChild(portalContainer);
      };
    }
  }, [portalContainer]);

  React.useEffect(() => {
    if (open) {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(overlayAnim, {
          toValue: 0.5,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 100,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(overlayAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [open]);

  if (!open || !portalContainer) return null;

  const content = (
    <View style={styles.root}>
      <TouchableWithoutFeedback onPress={onClose}>
        <Animated.View 
          style={[
            styles.overlay,
            {
              opacity: overlayAnim,
            }
          ]} 
        />
      </TouchableWithoutFeedback>
      
      <Animated.View
        style={[
          styles.drawer,
          {
            transform: [
              {
                translateX: slideAnim.interpolate({
                  inputRange: [0, 100],
                  outputRange: [0, 500],
                }),
              },
            ],
          },
        ]}
      >
        <View style={styles.drawerContent}>
          {showBreakup ? (
            <View>
              <View style={styles.header}>
                <TouchableOpacity onPress={onClose} style={styles.backButton}>
                  <Icon 
                    icon="tabler:arrow-left"
                    style={styles.backIcon}
                    color={globalConfigurableData.primaryTextColor1}
                    width="24"
                    height="24"
                  />
                </TouchableOpacity>
              </View>
              <View style={styles.breakupContent}>
                <Text style={styles.breakupTitle}>Amount Breakup</Text>
                <Text style={styles.breakupSubtitle}>Here's your real money balance summary</Text>
                
                <View style={styles.breakupTable}>
                  <View style={[styles.breakupRow, styles.breakupRowFirst]}>
                    <Text style={styles.breakupLabel}>Deposit Balance</Text>
                    <RupeeVal
                      customStyle={styles.breakupValue}
                      rupeeVal={breakupData.depositBalance}
                      isZeroCheck={true}
                    />
                  </View>
                  <View style={styles.breakupRow}>
                    <Text style={styles.breakupLabel}>Promo Balance</Text>
                    <RupeeVal
                      customStyle={styles.breakupValue}
                      rupeeVal={breakupData.promoBalance}
                      isZeroCheck={true}
                    />
                  </View>
                  <View style={[styles.breakupRow, styles.breakupRowLast]}>
                    <Text style={styles.breakupLabel}>Winning Balance</Text>
                    <RupeeVal
                      customStyle={styles.breakupValue}
                      rupeeVal={breakupData.winningBalance}
                      isZeroCheck={true}
                    />
                  </View>
                </View>
              </View>
            </View>
          ) : (
            <TransactionScreen 
              transaction={transaction} 
              typeTxn={typeTxn}
            />
          )}
        </View>
      </Animated.View>
    </View>
  );

  return ReactDOM.createPortal(content, portalContainer);
};

const styles = StyleSheet.create({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'black',
  },
  drawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '33%',
    backgroundColor: globalConfigurableData.cardBgColor,
    boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
  },
  drawerContent: {
    height: '100%',
    overflow: 'auto',
    backgroundColor: globalConfigurableData.cardBgColor,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: GlobalScreens.Wallet.transactionBox,
    borderTopRightRadius: 8,
  },
  backButton: {
    padding: 4,
  },
  backIcon: {
    width: 24,
    height: 24,
  },
  breakupContent: {
    padding: 24,
    backgroundColor: globalConfigurableData.cardBgColor,
  },
  breakupTitle: {
    ...globalFontStyle.appFontTitle,
    marginBottom: 10,
    color: globalConfigurableData.primaryTextColor1,
  },
  breakupSubtitle: {
    ...globalFontStyle.appFontSubText,
    marginBottom: 20,
    color: globalConfigurableData.secondaryTextColor1,
  },
  breakupTable: {
    width: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  breakupRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: globalConfigurableData.borderColor,
  },
  breakupRowFirst: {
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  breakupRowLast: {
    borderBottomWidth: 0,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  breakupLabel: {
    ...globalFontStyle.appFontSubText,
    color: globalConfigurableData.primaryTextColor1,
  },
  breakupValue: {
    ...globalFontStyle.appFontSubText,
    color: globalConfigurableData.primaryTextColor1,
  },
});

export default TransactionDrawer; 